import { useCallback, useState } from 'react';
import {
	fireOperationalAnalytics,
	fireTrackAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { CSV_FILE_UPLOAD_TYPE, CSV_MAX_SIZE_BYTES } from '../../../common/constants';
import { useUploadCsv } from '../../../services/upload-csv';
import {
	useMappingState,
	useMigrationId,
	useProjectData,
	useUploadedFileName,
} from '../../import-state';
import { useSource } from '../../import-state/selectors/source';

const isCSVFile = (selectedFileType: string) => selectedFileType === CSV_FILE_UPLOAD_TYPE;

export const useCSVDropzone = () => {
	const [isFileBrowserOpen, setIsFileBrowserOpen] = useState<boolean>(false);
	const [isFileNotCSV, setIsFileNotCSV] = useState<boolean>(false);
	const [isFileTooLarge, setIsFileTooLarge] = useState<boolean>(false);
	const [source] = useSource();
	const { isFileUploading, hasErrorOccurred, uploadCsv } = useUploadCsv(source);
	const [, { setMigrationId }] = useMigrationId();
	const [, { setMappingState, resetProjectData }] = useMappingState();
	const [uploadedFileName, { setUploadedFileName }] = useUploadedFileName();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [{ key }] = useProjectData();

	const onReceiveFile = useCallback(
		(file: File) => {
			setMappingState(null);
			fireUIAnalytics(createAnalyticsEvent({}), 'csvFile uploading', { fileSize: file.size });
			return uploadCsv(file, key).then((migrationId) => {
				setMigrationId(migrationId);
				resetProjectData();
				if (migrationId) {
					setUploadedFileName(file?.name ?? null);
					fireTrackAnalytics(createAnalyticsEvent({}), 'csvFile uploaded', {
						fileSize: file.size,
					});
				}
			});
		},
		[
			createAnalyticsEvent,
			key,
			resetProjectData,
			setMappingState,
			setMigrationId,
			setUploadedFileName,
			uploadCsv,
		],
	);

	const onRemoveFile = useCallback(() => {
		setUploadedFileName(null);
	}, [setUploadedFileName]);

	const onCloseFileBrowser = useCallback(() => setIsFileBrowserOpen(false), [setIsFileBrowserOpen]);

	const onOpenFileBrowser = useCallback(() => setIsFileBrowserOpen(true), [setIsFileBrowserOpen]);

	const onFileDraggedAndDropped = useCallback(
		(selectedFile: File) => {
			if (!isCSVFile(selectedFile.type)) {
				setIsFileNotCSV(true);
				return Promise.resolve();
			}
			setIsFileNotCSV(false);

			if (selectedFile.size > CSV_MAX_SIZE_BYTES) {
				setIsFileTooLarge(true);
				fireOperationalAnalytics(createAnalyticsEvent({}), 'csvFile exceedsLimit', {
					fileSize: selectedFile.size,
				});
				return Promise.resolve();
			}

			setIsFileTooLarge(false);
			return onReceiveFile(selectedFile);
		},
		[createAnalyticsEvent, onReceiveFile],
	);

	return {
		isFileBrowserOpen,
		isFileNotCSV,
		isFileTooLarge,
		isFileUploading,
		hasErrorOccurred,
		uploadedFileName,
		onReceiveFile,
		onRemoveFile,
		onCloseFileBrowser,
		onOpenFileBrowser,
		onFileDraggedAndDropped,
	};
};
